import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Close,
  Person,
  Email,
  Phone,
  LocationOn,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import { FlexListContainer, LabelItem, ValueItem } from "../../../molecules/FlexList";
import { useLocations } from "../../../stores/User/user.queries";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import type { UserDto } from "../../../api-client";

interface UserOverviewProps {
  /** The user data to display */
  user: UserDto;
  /** Callback to close/deselect this user */
  onClose: () => void;
}

/**
 * Read-only summary card for a user, shown in the left panel.
 * Modeled after PatientOverview — displays key demographic info at a glance.
 */
const UserOverview: React.FC<UserOverviewProps> = ({ user, onClose }) => {
  const { data: locations } = useLocations();

  // "Standort" surfaces the user's primary work location
  // (`therapistLocationId`). The previously-displayed `locationId` is the
  // last-selected viewing location and is only worth showing when it has
  // drifted from the primary — see the conditional row below.
  const primaryLocationName =
    locations?.find((loc) => loc.id === user?.therapistLocationId)?.name ||
    user?.therapistLocationId ||
    "–";
  const selectedLocationName =
    locations?.find((loc) => loc.id === user?.locationId)?.name || user?.locationId;
  const showSelectedLocation =
    !!user?.locationId &&
    user?.locationId !== user?.therapistLocationId;

  // Build role labels
  const roleLabels =
    user?.roles?.map((role: any) => role.name || role.id).join(", ") || "–";

  // Active/deleted status
  const isDeleted = !!user?.deletedAt;
  const statusLabel = isDeleted ? "Inaktiv" : "Aktiv";
  const statusColor = isDeleted ? "error" : "success";

  // Initials for avatar – handled by MobiliAvatar

  return (
    // flex column + flexGrow on CardContent so the themed CardContent background
    // fills the whole stretched card instead of ending where the content ends.
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeader
        avatar={
          <MobiliAvatar
            user={user}
            sx={{ width: 48, height: 48, fontSize: "1.2rem" }}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {user?.firstName} {user?.lastName}
          </Typography>
        }
        subheader={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Chip
              label={statusLabel}
              color={statusColor}
              size="small"
              variant="outlined"
            />
            {user?.nickName && (
              <Chip
                label={user.nickName}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
          </Stack>
        }
        action={
          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <Close />
          </IconButton>
        }
        sx={{ pb: 0 }}
      />

      <CardContent sx={{ padding: 1, pt: 2, flexGrow: 1 }}>
        <FlexListContainer>
          <LabelItem label="E-Mail" />
          <ValueItem value={user?.email || "–"} />

          <LabelItem label="Telefon" />
          <ValueItem value={user?.phone || "–"} />

          <LabelItem label="Benutzername" />
          <ValueItem value={user?.username || "–"} />

          <LabelItem label="Rollen" />
          <ValueItem value={roleLabels} />

          <LabelItem label="Standort" />
          <ValueItem value={primaryLocationName} />

          {showSelectedLocation && (
            <>
              <LabelItem label="Anzeige-Standort" />
              <ValueItem value={selectedLocationName} />
            </>
          )}

          {user?.lastWorkDay && (
            <>
              <LabelItem label="Letzter Arbeitstag" />
              <ValueItem
                value={new Date(user.lastWorkDay).toLocaleDateString("de-DE")}
              />
            </>
          )}
        </FlexListContainer>
      </CardContent>
    </Card>
  );
};

export default UserOverview;
